import type { Article } from '../types'

export const article: Article = {
  meta: {
    slug: 'svaty-martin-husa-a-vino',
    title: 'Proč se svatomartinské otevírá v 11:11 a další otázky nad husou',
    metaTitle: 'Svatý Martin: proč víno v 11:11 a proč husa',
    perex:
      'Proč zrovna jedenáct hodin jedenáct minut? Je každé mladé víno svatomartinské? A kolikrát Martin opravdu přijel na bílém koni? Šest otázek a odpovědí k nejchutnějšímu svátku podzimu a tip, kde ho oslavit.',
    coverImage: 'https://images.unsplash.com/photo-1626108436042-b59dcdbdef3b?w=1200&q=80&auto=format&fit=crop',
    coverImageAlt: 'Dvě sklenky červeného vína při přípitku u hořícího krbu',
    publishedAt: '2026-11-05',
    author: 'Redakce Mátevolno',
    tags: ['svatý Martin', 'podzim', 'víno', 'tradice'],
  },
  content: `Svatý Martin letos připadá na středu. Vinaři otevřou první lahve v 11:11, restaurace upečou husu a půlka republiky bude v pátek hledat, kde si to vynahradit o víkendu. Než zvednete sklenku, tady je šest věcí, které o Martinovi možná nevíte.

## 1. Proč zrovna v 11:11?

Protože 11. 11. Jedenáctá hodina a jedenáctá minuta jedenáctého dne jedenáctého měsíce je prostě hezká symetrie a vinaři z ní udělali rituál. Oficiálně platí jen to, že svatomartinské víno se smí začít prodávat 11. listopadu. Otevírání přesně v 11:11 je tradice, kterou si obchody, sklepy a vinotéky dodržují samy.

Je to česká odpověď na francouzské [Beaujolais nouveau](https://cs.wikipedia.org/wiki/Beaujolais_nouveau), které se otevírá každý třetí čtvrtek v listopadu. My máme náskok. Letos o osm dní.

## 2. Je každé mladé víno svatomartinské?

Není. [Svatomartinské](https://cs.wikipedia.org/wiki/Svatomartinsk%C3%A9_v%C3%ADno) je ochranná známka, kterou od roku 2005 spravuje Vinařský fond. Víno musí být z hroznů vypěstovaných v Česku, musí projít hodnoticí komisí s minimálně 80 body ze sta a smí být jen z několika odrůd:

| Barva | Povolené odrůdy |
| --- | --- |
| Bílé | Müller Thurgau, Veltlínské červené rané, Muškát moravský |
| Růžové a červené | Modrý Portugal, Svatovavřinecké, Zweigeltrebe |

Mladé víno z jiné odrůdy klidně může být dobré. Jen se nesmí jmenovat svatomartinské.

## 3. Proč se k tomu jí husa?

Podle legendy za to můžou samy husy. Martin z Tours prý nechtěl být biskupem a schoval se před lidmi, kteří ho přišli zvolit, v husinci. Husy ho kejháním prozradily a od té doby je za to lidé na jeho svátek pečou.

Praktičtější vysvětlení je kalendář. Svatý Martin byl posledním hodovacím dnem před zhruba čtyřicetidenním půstem, který kdysi předcházel Vánocům. Co se nesnědlo teď, čekalo až na Štědrý den. Hospodáři navíc na Martina končili hospodářský rok, platili čeleď a nemělo smysl krmit přes zimu husy, které se stejně snědí.

## 4. Přijede opravdu na bílém koni?

Pranostika slibuje sníh, statistika moc ne. Podle [ČT24](https://ct24.ceskatelevize.cz/clanek/pocasi/martin-na-bilem-koni-letos-neprijede-tato-pranostika-patri-mezi-ty-nespolehlive-42043) padá na Martina nový sníh jen v menšině let a pranostika patří mezi ty nejméně spolehlivé. Opravdu bílý Martin byl naposledy v roce 2016, kdy napadly centimetry sněhu i v Praze. V roce 2009 zase připadlo na Lysé hoře v Beskydech čtyřicet centimetrů.

Pravda na pranostice ale je. První sněžení přichází do nížin v průměru mezi 11. a 20. listopadem, jen sníh obvykle ještě nevydrží. Martin tedy spíš přijíždí na koni šedém, blátivém a trochu mokrém.

Pokud chcete bílého koně vidět na vlastní oči, máte větší šanci na horách. Třeba v [Krkonoších](/pobyty/krkonose) nebo na [Šumavě](/pobyty/sumava) bývá v polovině listopadu první sníh častěji než kde jinde.

## 5. Dá se z husy předpovědět zima?

Lidová meteorologie tvrdí, že ano. Po večeři se podívejte na prsní kost pečené husy. Když je bílá, bude zima se sněhem. Když je tmavá, čeká vás zima blátivá. Úspěšnost metody nikdo nezměřil, ale je to nejchutnější předpověď počasí, jakou znáte.

K husímu stolu patřily i [svatomartinské rohlíčky](https://www.denik.cz/z_domova/svatomartinske-hody-tradice-husa-vino-historie-martin-na-bilem-koni.html) plněné mákem nebo povidly. Tvarem připomínají podkovu Martinova koně a dívky je kdysi dávaly svým nápadníkům.

## 6. Kde ho oslavit, když je Martin ve středu?

Letos se dá slavit dvakrát. Martinský víkend 13.–15. listopadu navazuje na státní svátek 17. listopadu, který padá na úterý. Stačí si vzít pondělí a máte čtyři dny volna, během kterých jsou sklepy plné mladého vína a restaurace ještě mají husu na lístku.

Nejjistější volba je [jižní Morava](/pobyty/jizni-a-stredni-morava). Na Mikulovsku, Pálavě nebo na Znojemsku se o svatomartinském víkendu otevírají sklepy a ochutnávky jsou ve vinařských obcích skoro na každém rohu. Z chalupy se dá do sklepa dojít pěšky a zpátky nikoho nemusíte vézt autem.

![Dřevěné sudy s vínem ve sklepě](https://images.unsplash.com/photo-1566467021572-37fbefe8fcb2?w=1200&q=80&auto=format&fit=crop)

Kdo nechce na Moravu, udělá si Martina kdekoli, kde je trouba na husu a krb na večer. Na čtyři dny ve dvou se hodí [romantické pobyty](/pobyty/romanticke-pobyty), pro partu přátel velká [chalupa s krbem](/pobyty/s-krbem).

::nabidky{region="jizni-a-stredni-morava" text="Prohlédnout pobyty na jižní Moravě"}

## Na zdraví, Martine

Svatý Martin je poslední velký podzimní svátek, kdy se ještě dá jet na chalupu bez zimních pneumatik a bez vánočního shonu. Husa, láhev mladého vína a oheň v krbu. Víc k tomu potřeba není.

::cta{text="Najít volný termín na svatomartinský víkend"}`,
}
